import React from 'react';
import {i18n} from '@netx/core-next/locales';
import clsx from 'clsx';
import {faTimes} from '@fortawesome/pro-solid-svg-icons';
import {
	DialogTitle as MuiDialogTitle,
	type DialogTitleProps as MuiDialogTitleProps,
} from '@mui/material';
import IconButton from '@netx/core-components/IconButton';
import {
	composeClasses,
	styled,
	type WithClasses,
	type WithMuiPropOverrides,
} from '@netx/core-components/Theme';
import {Title, type TitleProps} from '../Title';
import {
	dialogTitleClasses,
	getDialogTitleUtilityClass,
	DIALOG_TITLE_CLASS_NAME,
	type DialogTitleClasses,
} from './dialogTitleClasses';

type DialogTitleOwnerProps = WithClasses<
	DialogTitleClasses,
	{
		hasIcon?: boolean;
	}
>;

const useUtilityClasses = (ownerState: DialogTitleOwnerProps) => {
	const {classes, hasIcon} = ownerState;
	const slots = {
		root: ['root', hasIcon && 'hasIcon'],
		actions: ['actions'],
		controls: ['controls'],
		icon: ['icon'],
		title: ['title'],
	};
	return composeClasses(slots, getDialogTitleUtilityClass, classes);
};

const DialogTitleRoot = styled(MuiDialogTitle, {
	name: DIALOG_TITLE_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	alignItems: 'center',
	overflow: 'hidden',
	[`&.${dialogTitleClasses.hasIcon}`]: {
		paddingLeft: theme.spacing(2),
	},
}));

const DialogTitleText = styled(Title, {
	name: DIALOG_TITLE_CLASS_NAME,
	slot: 'Title',
})(({theme}) => ({
	flex: '1 1 auto',
	overflow: 'hidden',
}));

const DialogTitleActions = styled('div', {
	name: DIALOG_TITLE_CLASS_NAME,
	slot: 'Title',
})(({theme}) => ({
	flex: '0 0 auto',
	marginLeft: theme.spacing(1),
}));

const DialogTitleControls = styled('div', {
	name: DIALOG_TITLE_CLASS_NAME,
	slot: 'Controls',
})(({theme}) => ({}));

export type DialogTitleProp =
	| string
	| {
			primary: string;
			secondary?: string;
	  };

export interface DialogTitleProps
	extends Omit<
			WithMuiPropOverrides<MuiDialogTitleProps>,
			'classes' | 'variant'
		>,
		Omit<TitleProps, 'classes'>,
		WithClasses<
			any,
			{
				disabled?: boolean;
				onClose?: () => void;
				controls?: React.ReactNode;
			}
		> {}

const DialogTitleInner = ({
	classes: _classes,
	className,
	actionButton,
	controls,
	disabled,
	icon,
	loading,
	primary,
	secondary,
	onClose,
	PrimaryTitleProps,
	SecondaryTitleProps,
	...props
}: DialogTitleProps) => {
	const ownerState: DialogTitleOwnerProps = {
		classes: _classes,
		className,
		hasIcon: icon != null,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<DialogTitleRoot {...props} className={clsx(classes.root, className)}>
			<DialogTitleText
				data-testid="dialog-title-text"
				actionButton={actionButton}
				icon={icon}
				loading={loading}
				primary={primary}
				secondary={secondary}
				PrimaryTitleProps={{
					variant: 'h6',
					...PrimaryTitleProps,
				}}
				SecondaryTitleProps={SecondaryTitleProps}
			/>
			{controls ? (
				<DialogTitleControls
					data-testid="dialog-title-controls"
					className={classes.controls}
				>
					{controls}
				</DialogTitleControls>
			) : null}
			{onClose ? (
				<DialogTitleActions
					data-testid="dialog-title-actions"
					className={classes.actions}
				>
					<IconButton
						data-testid="dialog-title-close"
						disabled={disabled}
						label={i18n.t('i18n.close')}
						edge="end"
						icon={faTimes}
						color="inherit"
						onClick={onClose}
					/>
				</DialogTitleActions>
			) : null}
		</DialogTitleRoot>
	);
};

export const DialogTitlePrimitive = DialogTitleInner;

export const DialogTitle = DialogTitlePrimitive;

export default DialogTitle;
